/**
 * The payload GET /api/v1/briefing/today actually returns.
 *
 * HAND-WRITTEN, and that is the documented path rather than a shortcut. The
 * generated manifest (src/types/api-manifest.d.ts) records this endpoint as a
 * 500 and therefore has no shape for it — not because the endpoint is broken,
 * but because the manifest is recorded against a pg_dump of production taken
 * before migration 0398 created `morning_briefs`. A photograph cannot show what
 * did not exist when it was taken, so the read 42P01s under the harness and
 * 200s in production, where `npm start` migrates before the server accepts a
 * request. The entry will move 500 → 200 at the next fixture refresh.
 *
 * Written against fieldiq-api origin/main
 * src/services/morning-brief.service.ts (PR #698) and checked against live
 * `morning_briefs` rows for all ten active tenants on 2026-09-11.
 */

/**
 * The ticket statuses a brief counts. Mirrors BRIEF_TICKET_STATUSES in the API
 * service — note it does NOT include `archived`, which is in this repo's
 * TicketStatus union but outside the brief's vocabulary.
 */
export const BRIEF_TICKET_STATUSES = [
  'draft',
  'field_approved',
  'office_approved',
  'customer_approved',
  'customer_rejected',
  'rejected',
  'invoiced',
] as const;

export type BriefTicketStatus = (typeof BRIEF_TICKET_STATUSES)[number];

/**
 * Every status is present with an explicit 0 — the API enumerates rather than
 * deriving from a GROUP BY, so "no tickets are waiting" and "the brief forgot
 * to ask" are different values rather than the same `undefined`.
 */
export type TicketStatusCounts = Record<BriefTicketStatus, number>;

export interface BriefJob {
  jobId: string;
  jobNumber: string | null;
  status: string;
  wellName: string | null;
  customerName: string | null;
  /** ISO instant, or null. Set iff the office closed the job. */
  completedAt: string | null;
  /** ISO instant, or null. A hand declared the work done; NOT implied by status. */
  workFinishedAt: string | null;
  /** Every live ticket on the job, by status — not just the newest. */
  ticketCounts: TicketStatusCounts;
  /** A separate COUNT(*). Exceeding the sum of ticketCounts is the API's signal that a status outside the shared vocabulary exists. */
  ticketTotal: number;
}

export interface BriefFacts {
  /** What happened since the PREVIOUS brief — not a fixed 24 hours. */
  period: {
    jobsCompleted: BriefJob[];
    jobsWorkFinished: BriefJob[];
    /** Distinct jobs across both lists. NOT the sum — one job can be in both. */
    finishedJobCount: number;
    finishedWithoutTicket: number;
    ticketsByStatus: TicketStatusCounts;
  };
  /** The standing position, independent of the period. */
  standing: {
    /** All-time. PAR's 42, not the period's 1. */
    finishedWithoutTicket: number;
    ticketsByStatus: TicketStatusCounts;
    /** office_approved + customer_approved. */
    invoiceable: number;
  };
  /** Pending HITL items, which nothing outside the agent screen surfaces. */
  pendingAgentRuns: {
    total: number;
    byAgent: Record<string, number>;
  };
  lastNightWrap: { id: string; createdAt: string; summary: string } | null;
}

export interface StoredBrief {
  id: string;
  companyId: string;
  /** The TENANT's local calendar day, 'YYYY-MM-DD'. Not the viewer's. */
  briefDate: string;
  periodStart: string;
  periodEnd: string;
  timeZone: string;
  facts: BriefFacts;
  createdAt: string;
}

/**
 * `data: null` with a `reason` is a REAL answer, not an error: a tenant
 * provisioned after 07:00, or one whose first tick has not run. It is typed
 * here so a consumer cannot read the absence as an empty brief.
 */
export interface BriefingTodayResponse {
  success: boolean;
  data: StoredBrief | null;
  reason?: 'no_brief_yet';
}
